import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PROMO_RATE } from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAvailability } from '@/features/calls/hooks/useAvailability';
import { useCall } from '@/features/calls/context/CallContext';
import { FavoriteButton } from '@/features/favorites/components/FavoriteButton';
import { useMyAvailabilityWatches } from '@/features/availability-watch/hooks/useAvailabilityWatch';
import { NotifyWhenOnlineButton } from '@/features/availability-watch/components/NotifyWhenOnlineButton';
import { resolveUploadUrl } from '@/lib/uploads';
import { professionalStatusDisplay } from '@/features/professionals/lib/statusDisplay';
import type { PublicProfessional } from '../services/professional-public.service';
import { StarRating } from './StarRating';

function CardServiceIcon({ active, label, children }: { active: boolean; label: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  if (!active) return null;

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={label}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        onClick={() => setShow((v) => !v)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-colors hover:bg-neutral-200 hover:text-neutral-700"
      >
        {children}
      </button>
      {show && (
        <div role="tooltip" className="absolute left-1/2 top-full z-20 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg bg-brand-navy px-2.5 py-1 text-[11px] text-white shadow-lg">
          {label}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 rotate-45 bg-brand-navy" />
        </div>
      )}
    </div>
  );
}

function CardServiceIcons({ serviceChannels }: { serviceChannels?: { chat: boolean; audioCall: boolean; videoCall: boolean } }) {
  const chat = serviceChannels?.chat !== false;
  const audio = serviceChannels?.audioCall !== false;
  const video = serviceChannels?.videoCall !== false;
  if (!chat && !audio && !video) return null;

  return (
    <div className="flex items-center gap-1.5">
      <CardServiceIcon active={chat} label="Chat">
        <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </CardServiceIcon>
      <CardServiceIcon active={audio} label="Chiamata audio">
        <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
        </svg>
      </CardServiceIcon>
      <CardServiceIcon active={video} label="Videochiamata">
        <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
        </svg>
      </CardServiceIcon>
    </div>
  );
}

interface ConsultantCardProps {
  professional: PublicProfessional;
  showPromoPrice?: boolean;
}

export function ConsultantCard({ professional, showPromoPrice }: ConsultantCardProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { getStatus } = useAvailability();
  const { initiateCall } = useCall();
  const { data: watchData } = useMyAvailabilityWatches(isAuthenticated);
  const isWatching = !!watchData?.watches?.some((w) => w.professionalId === professional.id);
  const [imgError, setImgError] = useState(false);
  const user = professional.userId;
  const fullName = `${user?.profile?.firstName ?? ''} ${user?.profile?.lastName ?? ''}`.trim();
  const onlineStatus = getStatus(professional.id, professional.onlineStatus);
  const isAvailable = onlineStatus === 'online';
  const status = professionalStatusDisplay(onlineStatus);
  const callEnabled =
    professional.serviceChannels?.audioCall !== false || professional.serviceChannels?.videoCall !== false;
  const chatEnabled = professional.serviceChannels?.chat !== false;

  // Paid chat: logged-out users have no call socket, so initiateCall would flip
  // the overlay into a non-idle chat state with no way back. Gate behind auth.
  const handleChat = () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/consultant/${professional.id}`)}`);
      return;
    }
    initiateCall(professional.id, 'chat', fullName);
  };

  // Defensive: a professional whose populated user relation is missing
  // (deleted/orphaned account) would otherwise crash the whole list.
  if (!user?.profile) return null;

  return (
    <div className="group relative overflow-hidden rounded-xl border border-neutral-200 bg-white/60 transition-all duration-300 hover:border-brand-teal/30 hover:bg-white hover:shadow-[0_4px_24px_rgba(24,153,170,0.08)] hover:scale-[1.005]">
      <div className="flex flex-col sm:flex-row">
        {/* Left: Avatar + Status */}
        <div className="relative flex flex-col items-center justify-center gap-2 px-5 py-5 sm:w-40 sm:shrink-0">
          <div className="relative">
            {user.profile?.avatar && !imgError ? (
              <img
                src={resolveUploadUrl(user.profile.avatar)}
                alt={fullName}
                className="h-20 w-20 rounded-full border-2 border-brand-cyan/20 object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-brand-cyan/20 bg-neutral-100">
                <img src="/images/logo-navy.png" alt="" className="h-12 w-12 object-contain opacity-60" />
              </div>
            )}
            {/* Pulsating status dot */}
            <span className="absolute bottom-1 right-1">
              <span className={`block h-4 w-4 rounded-full border-[2.5px] border-white ${status.dotClass}`} />
              {status.pulse && (
                <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/40" />
              )}
            </span>
          </div>
          {/* Status label */}
          <span
            className={`rounded-full px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${status.badgeClass}`}
          >
            {status.label}
          </span>
        </div>

        {/* Center: Info */}
        <div className="flex flex-1 flex-col justify-center border-t border-neutral-100 px-5 py-4 sm:border-l sm:border-t-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Link
              to={`/consultant/${professional.id}`}
              className="text-lg font-semibold text-brand-navy transition-colors hover:text-brand-orange"
            >
              {fullName}
            </Link>
            <FavoriteButton professionalId={professional.id} size="sm" />
          </div>

          {professional.title && (
            <p className="mb-2 text-sm text-neutral-500 line-clamp-1">{professional.title}</p>
          )}

          {/* Specializations */}
          <div className="mb-3 flex flex-wrap gap-1.5">
            {professional.specializations.slice(0, 4).map((spec) => (
              <span
                key={spec}
                className="rounded-full bg-brand-teal/15 px-2.5 py-0.5 text-[11px] font-medium text-brand-teal-400"
              >
                {spec}
              </span>
            ))}
          </div>

          {/* Rating + Reviews + Service Channels */}
          <div className="flex items-center gap-3">
            <StarRating rating={professional.rating ?? 0} reviewCount={professional.reviewCount} />
            {professional.experience != null && professional.experience > 0 && (
              <span className="text-xs text-neutral-400">
                {professional.experience} {professional.experience === 1 ? 'anno' : 'anni'} exp.
              </span>
            )}
            <CardServiceIcons serviceChannels={professional.serviceChannels} />
          </div>
        </div>

        {/* Right: Price + CTA */}
        <div className="flex flex-row items-center gap-3 border-t border-neutral-100 px-5 py-4 sm:flex-col sm:justify-center sm:border-l sm:border-t-0 sm:w-44 sm:shrink-0">
          {/* Price — the per-minute rate applies to paid chat too, so a
              chat-only consultant must still show it */}
          {professional.costPerMinute != null && (callEnabled || chatEnabled) && (
            <div className="text-center">
              {showPromoPrice ? (
                <>
                  <span className="text-sm font-medium text-neutral-400 line-through">{professional.costPerMinute.toFixed(2)}&euro;</span>
                  <p>
                    <span className="text-2xl font-bold text-brand-orange">{PROMO_RATE.toFixed(2).replace('.', ',')}&euro;</span>
                  </p>
                </>
              ) : (
                <p className="text-2xl font-bold text-brand-orange">
                  {professional.costPerMinute.toFixed(2)}<span className="text-sm font-normal text-brand-orange/60"> &euro;</span>
                </p>
              )}
              <p className="text-[11px] text-neutral-400">al minuto</p>
            </div>
          )}

          {/* CTA Buttons — online: Chiama + Chat (paid chat session).
              offline/busy: only "Avvisami" */}
          <div className="flex flex-1 items-center gap-2 sm:flex-col sm:w-full">
            {isAvailable ? (
              <>
                {callEnabled && (
                  <Link
                    to={`/consultant/${professional.id}`}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-orange px-4 py-2.5 text-sm font-semibold text-white shadow-[0_0_15px_rgba(235,114,35,0.2)] transition-all duration-200 hover:bg-brand-orange/90 hover:shadow-[0_0_20px_rgba(235,114,35,0.3)] min-h-[44px] sm:w-full"
                  >
                    <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                    Chiama
                  </Link>
                )}
                {chatEnabled && (
                  <button
                    type="button"
                    onClick={handleChat}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-brand-teal/30 bg-brand-teal/10 px-4 py-2.5 text-sm font-semibold text-brand-teal transition-all duration-200 hover:bg-brand-teal/20 hover:border-brand-teal/50 min-h-[44px] sm:w-full"
                  >
                    <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Chat
                  </button>
                )}
              </>
            ) : (
              <NotifyWhenOnlineButton
                professionalId={professional.id}
                professionalName={fullName}
                isWatching={isWatching}
                className="flex-1 sm:w-full"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
